import { useEffect, useId, useState } from 'react'
import { router } from '@inertiajs/react'

import { Switch } from '@components/atoms/Switch'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('configurations')

export type SettingSwitch = { key: string; checked: boolean }

type SwitchRowProps = { action: string; settingKey: string; checked: boolean; label: string; hint?: string }

function SwitchRow({ action, settingKey, checked, label, hint }: SwitchRowProps) {
  const id = useId()
  const [on, setOn] = useState(checked)
  const [failed, setFailed] = useState(false)

  // Follow the server value when a page reload brings a change made elsewhere (another admin's flip).
  useEffect(() => setOn(checked), [checked])

  const flip = (next: boolean) => {
    let saved = false
    setOn(next)
    setFailed(false)
    router.patch(
      `${action}/switches/${settingKey}`,
      { value: next },
      {
        // Without this a flip on another row cancels this visit and the row reads as failed.
        async: true,
        preserveScroll: true,
        onSuccess: () => {
          saved = true
        },
        onFinish: () => {
          if (saved) return
          setOn(!next)
          setFailed(true)
        },
      }
    )
  }

  const describedBy = [hint && `${id}-hint`, failed && `${id}-error`].filter(Boolean).join(' ') || undefined

  return (
    <li className="tw:flex tw:items-start tw:justify-between tw:gap-4 tw:border-t tw:border-gray-cool-100 tw:px-5 tw:py-4 tw:first:border-t-0">
      <div className="tw:min-w-0">
        <label
          htmlFor={id}
          className="tw:m-0 tw:block tw:cursor-pointer tw:text-sm tw:font-medium tw:text-gray-cool-900"
        >
          {label}
        </label>
        {hint && (
          <p id={`${id}-hint`} className="tw:m-0 tw:text-sm tw:text-gray-cool-600">
            {hint}
          </p>
        )}
        <div role="status">
          {failed && (
            <p id={`${id}-error`} className="tw:m-0 tw:pt-1 tw:text-sm tw:text-red-600">
              {t('switch_failed')}
            </p>
          )}
        </div>
      </div>
      <Switch id={id} aria-describedby={describedBy} checked={on} onCheckedChange={flip} />
    </li>
  )
}

type SettingsSwitchesProps = {
  action: string
  switches: SettingSwitch[]
  // The page's scoped translation; each switch reads `switches.<key>.label` and an optional `.hint` from it.
  t: ReturnType<typeof scopedTranslation>
}

export function SettingsSwitches({ action, switches, t: pageT }: SettingsSwitchesProps) {
  return (
    <ul className="tw:m-0 tw:list-none tw:p-0">
      {switches.map(({ key, checked }) => (
        <SwitchRow
          key={key}
          action={action}
          settingKey={key}
          checked={checked}
          label={pageT(`switches.${key.toLowerCase()}.label`)}
          hint={pageT(`switches.${key.toLowerCase()}.hint`, { defaultValue: '' }) || undefined}
        />
      ))}
    </ul>
  )
}
